import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, FolderKanban, Globe, Info, Send, Shapes, TrendingUp, Wand2, type LucideIcon } from 'lucide-react';

import { cn } from '../../lib/utils';
import type { PortfolioKpis } from '../../types/portfolio';
import { monthLabel, tint } from './portfolioFormat';

interface TileProps {
  icon: LucideIcon;
  color: string;
  label: string;
  hint: string;
  value: number;
  trend?: string;
  caption: string;
  compact?: boolean;
}

/** A tinted KPI tile: uppercase caption with an info hint, big figure, optional trend, context line, watermark icon. */
const KpiTile: React.FC<TileProps> = ({ icon: Icon, color, label, hint, value, trend, caption, compact }) => (
  <div
    className={cn('relative min-w-0 overflow-hidden rounded-lg border', compact ? 'px-3 py-2.5' : 'px-4 py-3.5')}
    style={{ background: tint(color, 0.08), borderColor: tint(color, 0.25) }}
  >
    <div className="flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-foreground">
      <span className="truncate">{label}</span>
      <span title={hint} aria-label={hint} className="shrink-0 cursor-help text-muted-foreground">
        <Info className="h-3 w-3" />
      </span>
    </div>
    <div className="mt-1 flex items-baseline gap-2">
      <span className={cn('font-bold leading-none text-foreground', compact ? 'text-2xl' : 'text-3xl')}>{value}</span>
      {trend && (
        <span className="flex items-center gap-0.5 text-xs font-semibold" style={{ color }}>
          <TrendingUp className="h-3.5 w-3.5" aria-hidden />
          {trend}
        </span>
      )}
    </div>
    <div className="mt-1.5 truncate text-xs text-muted-foreground" title={caption}>
      {caption}
    </div>
    <Icon
      aria-hidden
      className={cn('pointer-events-none absolute -bottom-3 -right-3', compact ? 'h-12 w-12' : 'h-16 w-16')}
      strokeWidth={1.5}
      style={{ color, opacity: 0.18 }}
    />
  </div>
);

/**
 * The six portfolio KPIs (plan §7.3), over every project of the organization:
 * deposited, domains, motifs, analysed, proposed, published.
 */
export const PortfolioKpiTiles: React.FC<{ kpis: PortfolioKpis; compact?: boolean; className?: string }> = ({
  kpis,
  compact,
  className,
}) => {
  const { t } = useTranslation();
  const one = (n: number, key: string) => t(`portfolio.kpis.${key}.${n > 1 ? 'labelMany' : 'labelOne'}`);
  const pct = kpis.deposited ? Math.round((kpis.analysed / kpis.deposited) * 100) : 0;
  const pending = kpis.proposed;
  const firstMonth = kpis.first_deposit_at ? monthLabel(new Date(kpis.first_deposit_at)) : null;

  return (
    <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6', className)}>
      <KpiTile
        compact={compact}
        icon={FolderKanban}
        color="#007BFF"
        label={one(kpis.deposited, 'deposited')}
        hint={t('portfolio.kpis.deposited.hint')}
        value={kpis.deposited}
        trend={
          kpis.deposited_this_month > 0
            ? t('portfolio.kpis.deposited.trend', { n: kpis.deposited_this_month })
            : undefined
        }
        caption={firstMonth ? t('portfolio.kpis.deposited.since', { month: firstMonth }) : t('portfolio.kpis.deposited.none')}
      />
      <KpiTile
        compact={compact}
        icon={Building2}
        color="#AD72DB"
        label={one(kpis.domains, 'domains')}
        hint={t('portfolio.kpis.domains.hint')}
        value={kpis.domains}
        caption={
          kpis.top_domain
            ? t('portfolio.kpis.domains.top', { domain: kpis.top_domain, n: kpis.top_domain_count })
            : '—'
        }
      />
      <KpiTile
        compact={compact}
        icon={Shapes}
        color="#00B8A0"
        label={one(kpis.motifs, 'motifs')}
        hint={t('portfolio.kpis.motifs.hint')}
        value={kpis.motifs}
        caption={t(
          kpis.projects_in_motifs > 1 ? 'portfolio.kpis.motifs.groupedMany' : 'portfolio.kpis.motifs.groupedOne',
          { n: kpis.projects_in_motifs }
        )}
      />
      <KpiTile
        compact={compact}
        icon={Wand2}
        color="#0099CC"
        label={one(kpis.analysed, 'analysed')}
        hint={t('portfolio.kpis.analysed.hint')}
        value={kpis.analysed}
        trend={kpis.deposited ? `${pct} %` : undefined}
        caption={t('portfolio.kpis.analysed.caption')}
      />
      <KpiTile
        compact={compact}
        icon={Send}
        color="#E5A100"
        label={one(pending, 'proposed')}
        hint={t('portfolio.kpis.proposed.hint')}
        value={pending}
        caption={pending ? t('portfolio.kpis.proposed.pending', { n: pending }) : t('portfolio.kpis.proposed.none')}
      />
      <KpiTile
        compact={compact}
        icon={Globe}
        color="#04B600"
        label={one(kpis.published, 'published')}
        hint={t('portfolio.kpis.published.hint')}
        value={kpis.published}
        caption={t(
          kpis.qualified > 1 ? 'portfolio.kpis.published.qualifiedMany' : 'portfolio.kpis.published.qualifiedOne',
          { n: kpis.qualified }
        )}
      />
    </div>
  );
};

export default PortfolioKpiTiles;
